'use client';

import { createContext, useContext, ReactNode, useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { createClient } from '@/lib/supabase/browser';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  isAuthenticated: boolean;
  loading: boolean;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export { AuthContext };

export function AuthProvider({ children }: { children: ReactNode }) {
  const supabase = createClient();
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const refreshIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const TWENTY_FIVE_MINUTES_MS = 25 * 60 * 1000;

  useEffect(() => {
    let mounted = true;

    const initAuth = async () => {
      try {
        const { data: { session: sess } } = await supabase.auth.getSession();
        if (mounted) {
          setUser(sess?.user ?? null);
          setSession(sess ?? null);
        }
      } catch (error) {
        console.error('[AuthProvider] Error getting session:', error);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    initAuth();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event: string, sess: Session | null) => {
        if (!mounted) return;
        if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED' || event === 'USER_UPDATED') {
          setUser(sess?.user ?? null);
          setSession(sess ?? null);
        } else if (event === 'SIGNED_OUT') {
          setUser(null);
          setSession(null);
        }
        setLoading(false);
      }
    );

    refreshIntervalRef.current = setInterval(async () => {
      if (!mounted) return;
      const { data: { session: sess } } = await supabase.auth.getSession();
      if (sess) {
        await supabase.auth.refreshSession();
      }
    }, TWENTY_FIVE_MINUTES_MS);

    return () => {
      mounted = false;
      subscription.unsubscribe();
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
        refreshIntervalRef.current = null;
      }
    };
  }, [supabase]);

  const signOut = useCallback(async () => {
    try {
      await supabase.auth.signOut();
    } catch (error) {
      console.error('Error signing out:', error);
    }
  }, [supabase]);

  const value = useMemo(() => ({
    user,
    session,
    isAuthenticated: !!user,
    loading,
    signOut,
  }), [user, session, loading, signOut]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuthContext() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }
  return context;
}
